import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Provider, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import { store } from './store';
import { ThemeProvider } from './contexts/ThemeContext';
import { SidebarProvider } from './contexts/SidebarContext';
import { SizeProvider, useSize } from './contexts/SizeContext';
import { apiFetch } from './utils/streamApi';
import { syncAlertPollFromServer } from './utils/alertPollConfig';
import { initPushNotifications } from './utils/pushNotifications';
import useIsMobile from './utils/useIsMobile';
import Navbar  from './components/Navbar';
import Sidebar from './components/Sidebar';
import OfflineLocalModeBanner from './components/OfflineLocalModeBanner';
import SosActiveBanner from './components/SosActiveBanner';
import DevicesPausedBanner from './components/DevicesPausedBanner';
import UpdateBanner from './components/UpdateBanner';
import SupportAccessBanner from './components/SupportAccessBanner';
import LoginPage                   from './pages/LoginPage';
import ChangePasswordPage          from './pages/ChangePasswordPage';
import ResponderLoginPage          from './pages/ResponderLoginPage';
import ResponderChangePasswordPage from './pages/ResponderChangePasswordPage';
import SosListPage                 from './pages/SosListPage';
import LocalModePage                from './pages/LocalModePage';
import OfflineAlertsPage            from './pages/OfflineAlertsPage';
import GuidedSetupPage    from './pages/GuidedSetupPage';
import MyCamerasPage      from './pages/MyCamerasPage';
import DashboardPage      from './pages/DashboardPage';
import AlertsPage         from './pages/AlertsPage';
import SnapshotsPage      from './pages/SnapshotsPage';
import KnownFacesPage          from './pages/KnownFacesPage';
import AddKnownPersonPage      from './pages/AddKnownPersonPage';
import KnownSeenPage           from './pages/KnownSeenPage';
import BannedFacesPage         from './pages/BannedFacesPage';
import ManageRecordingsPage    from './pages/ManageRecordingsPage';
import AdminConfigPage            from './pages/AdminConfigPage';
import SchedulePage               from './pages/SchedulePage';
import ForensicReportPage        from './pages/ForensicReportPage';
import DetectionSettingsPage     from './pages/DetectionSettingsPage';
import DiagnosticsPage           from './pages/DiagnosticsPage';
import DetectionsPage            from './pages/DetectionsPage';
import AccessManagementPage      from './pages/AccessManagementPage';
import GuardPatrolPage           from './pages/GuardPatrolPage';
import PatrolReportPage          from './pages/PatrolReportPage';
import PatrolTestModePage        from './pages/PatrolTestModePage';
import GuardCheckinPage          from './pages/GuardCheckinPage';
import AttendanceConfigPage      from './pages/AttendanceConfigPage';
import MyAttendancePage          from './pages/MyAttendancePage';
import AttendanceReviewPage      from './pages/AttendanceReviewPage';
import PacsAccessPage            from './pages/PacsAccessPage';
import DeviceAlertsReportPage    from './pages/DeviceAlertsReportPage';
import LearningReportPage        from './pages/LearningReportPage';
import ConfigChangelogPage       from './pages/ConfigChangelogPage';
import SupportPage               from './pages/SupportPage';
import IndustrialOpsPage         from './pages/industrial/IndustrialOpsPage';
import IndustrialAccessPage      from './pages/industrial/IndustrialAccessPage';
import IndustrialAttendancePage  from './pages/industrial/IndustrialAttendancePage';
import IndustrialWallPage        from './pages/industrial/IndustrialWallPage';
import IndustrialModulesPage     from './pages/industrial/IndustrialModulesPage';
import IndustrialObjectCountPage from './pages/industrial/IndustrialObjectCountPage';
import IndustrialPPEPage         from './pages/industrial/IndustrialPPEPage';
import IndustrialCountReportPage from './pages/industrial/IndustrialCountReportPage';
import IndustrialFootfallPage   from './pages/industrial/IndustrialFootfallPage';
import IndustrialVehicleReportPage from './pages/industrial/IndustrialVehicleReportPage';
import IndustrialSeatsPage      from './pages/industrial/IndustrialSeatsPage';
import IndustrialSeatReportPage from './pages/industrial/IndustrialSeatReportPage';
import IndustrialCleaningZonesPage  from './pages/industrial/IndustrialCleaningZonesPage';
import IndustrialCleaningReportPage from './pages/industrial/IndustrialCleaningReportPage';
import IndustrialReportsPage     from './pages/industrial/IndustrialReportsPage';
import IndustrialVideoAnalyzerPage from './pages/industrial/IndustrialVideoAnalyzerPage';
import IndustrialShopAnalyticsPage from './pages/industrial/IndustrialShopAnalyticsPage';
import IndustrialShopAnalyticsReportPage from './pages/industrial/IndustrialShopAnalyticsReportPage';
import DevicesPage from './pages/DevicesPage';
import { canAccessMenu, refreshEntitlements, useEntitlements } from './utils/entitlements';

// PRVSK-65: how often an already-open session re-checks /ui/entitlements, so a
// role edit made in Users & Roles reaches other open sessions without them
// having to log out. Window focus (below) covers the common "came back to this
// tab" case near-instantly; this poll is the fallback for a tab left open and
// never refocused.
const ENTITLEMENTS_POLL_MS = 60_000;

function Guard({ children }) {
  const { isAuthenticated, resetRequired } = useSelector(s => s.auth);
  const synced = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !synced.current) {
      synced.current = true;
      syncAlertPollFromServer(apiFetch);
      initPushNotifications().catch(() => {});
    }
  }, [isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated) return;
    // Resolve product tier / role from the server so nav + routes gate on the
    // real account (falls back to entitlements.js defaults if the call fails).
    refreshEntitlements(apiFetch);
    const onFocus = () => refreshEntitlements(apiFetch);
    window.addEventListener('focus', onFocus);
    const interval = setInterval(() => refreshEntitlements(apiFetch), ENTITLEMENTS_POLL_MS);
    return () => {
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, [isAuthenticated]);
  if (!isAuthenticated)  return <Navigate to="/login"           replace />;
  if (resetRequired)     return <Navigate to="/change-password" replace />;
  return <>{children}<OfflineLocalModeBanner /><SosActiveBanner /></>;
}

function ResponderGuard({ children }) {
  const { isResponder, responderResetRequired } = useSelector(s => s.responder);
  if (!isResponder)            return <Navigate to="/responder/login"           replace />;
  if (responderResetRequired)  return <Navigate to="/responder/change-password" replace />;
  return children;
}

// Desktop-only: on a phone the Dashboard's Play / Pause Devices tiles do this.
function DevicesRoute() {
  const isMobile = useIsMobile();
  if (isMobile) return <Navigate to="/dashboard" replace />;
  return <Entitled menu="cameras" element={<DevicesPage />} />;
}

function Layout({ children }) {
  const { scale } = useSize();
  return (
    <div style={{ height: `${100 / scale}vh`, overflow: 'hidden', display: 'flex', flexDirection: 'column', background: 'var(--bg)', zoom: scale }}>
      <Navbar />
      <DevicesPausedBanner />
      <UpdateBanner />
      <SupportAccessBanner />
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Sidebar />
        <main style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', scrollbarGutter: 'stable' }}>{children}</main>
      </div>
    </div>
  );
}

function P({ element }) {
  return <Guard><Layout>{element}</Layout></Guard>;
}

// Route-level menu gate. Redirects to '/' if the logged-in role's menu
// permissions (from /ui/entitlements) don't include this page's menu key.
// PRVSK-65: subscribes via useEntitlements() so a role change that lands
// while this route is already open (poll/focus refetch, or an admin saving
// a role elsewhere) re-evaluates access immediately instead of only on the
// next route change — a revoked menu bounces the user out of a page that
// would otherwise sit there until it started 403-ing.
function Entitled({ menu, industrial, appSupport, element }) {
  const ent = useEntitlements();
  if (!canAccessMenu({ menu, industrial, appSupport }, ent)) return <Navigate to="/" replace />;
  return <Guard><Layout>{element}</Layout></Guard>;
}

// Same gate, no Layout — Guided Setup owns the whole screen. A sidebar and a
// navbar around "what are you protecting?" is console furniture for a customer
// who is not an operator yet.
function EntitledFullScreen({ menu, element }) {
  const ent = useEntitlements();
  if (!canAccessMenu({ menu }, ent)) return <Navigate to="/" replace />;
  return <Guard>{element}</Guard>;
}

// First-login redirect into Guided Setup. Fires once per session: a customer who
// deliberately leaves the wizard ("finish later") must not be dragged back into
// it on every navigation, but a fresh login still lands there until setup is
// actually finished.
const SETUP_SEEN = 'prvsk_setup_prompted';

function SetupGate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useSelector(s => s.auth);
  const checked = useRef(false);
  const alive   = useRef(true);
  const path    = useRef(location.pathname);
  path.current  = location.pathname;
  useEffect(() => {
    alive.current = true;          // StrictMode mounts twice; the second mount must undo the first's cleanup
    return () => { alive.current = false; };
  }, []);

  // Asked once per login. The reply is acted on even if the page has moved since:
  // on a phone "/" redirects to /dashboard (and on an industrial site to
  // /industrial) straight after login, and cancelling on that path change threw
  // away the "open setup" answer — with `checked` already set, the wizard then
  // never opened for that session (PRVSK-150). Only an unmount (logout) cancels.
  useEffect(() => {
    if (!isAuthenticated) { checked.current = false; return; }   // next login asks again
    if (checked.current) return;
    if (location.pathname.startsWith('/guided-setup')) return;
    checked.current = true;
    try { if (sessionStorage.getItem(SETUP_SEEN)) return; } catch { /* private mode */ }
    apiFetch('/ui/setup/state')
      .then(r => (r.ok ? r.json() : null))
      .then(d => {
        if (!alive.current || !d?.should_open) return;
        if (path.current.startsWith('/guided-setup')) return;   // already there
        try { sessionStorage.setItem(SETUP_SEEN, '1'); } catch { /* ignore */ }
        navigate('/guided-setup', { replace: true });
      })
      .catch(() => {});
  }, [isAuthenticated, location.pathname, navigate]);

  return null;
}

function RootRoute() {
  const isMobile = useIsMobile();
  const ent = useEntitlements();   // re-render once clientType/menus hydrate from /ui/entitlements
  // Industrial sites land on the Ops Console by default (desktop or mobile) — it is
  // their primary console, not the camera grid. Guard on menu access so a user who
  // lacks the industrial-ops menu doesn't ping-pong /industrial ↔ / (Entitled sends
  // inaccessible pages back to /).
  if (ent.clientType === 'industrial' && canAccessMenu({ menu: 'industrial-ops', industrial: true }, ent))
    return <Navigate to="/industrial" replace />;
  // The support login (PRVSK-109) is read-only and has no camera page: start on Diagnostics.
  if (ent.role === 'support') return <Navigate to="/diagnostics" replace />;
  if (isMobile) return <Navigate to="/dashboard" replace />;
  return <P element={<MyCamerasPage />} />;
}

function DashboardRoute() {
  const isMobile = useIsMobile();
  if (!isMobile) return <Navigate to="/" replace />;
  return <Guard><DashboardPage /></Guard>;
}

function AppRoutes() {
  return (
    <BrowserRouter>
      <SetupGate />
      <Routes>
        <Route path="/login"           element={<LoginPage />} />
        <Route path="/local-mode"      element={<LocalModePage />} />
        <Route path="/offline-alerts"  element={<OfflineAlertsPage />} />
        {/* In-app Local Mode (sidebar entry while the server is unreachable) —
            same cameras + saved alerts, inside the logged-in shell. */}
        <Route path="/local"           element={<P element={<LocalModePage embedded />} />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route path="/responder/login"           element={<ResponderLoginPage />} />
        <Route path="/responder/change-password" element={<ResponderGuard><ResponderChangePasswordPage /></ResponderGuard>} />
        <Route path="/responder/sos"             element={<ResponderGuard><SosListPage /></ResponderGuard>} />
        <Route path="/responder"                 element={<Navigate to="/responder/login" replace />} />
        <Route path="/"                element={<RootRoute />} />
        <Route path="/guided-setup"    element={<EntitledFullScreen menu="guided-setup" element={<GuidedSetupPage />} />} />
        <Route path="/cameras"         element={<Entitled menu="cameras"    element={<MyCamerasPage />} />} />
        <Route path="/dashboard"       element={<DashboardRoute />} />
        {/* Same permission as Cameras: the mobile Dashboard offers these to anyone signed in. */}
        <Route path="/devices"         element={<DevicesRoute />} />
        <Route path="/alerts"          element={<Entitled menu="alerts"     element={<AlertsPage />} />} />
        <Route path="/snapshots"       element={<P element={<SnapshotsPage />} />} />
        <Route path="/faces/known"     element={<Entitled menu="known-faces" element={<KnownFacesPage />} />} />
        <Route path="/faces/add"       element={<Entitled menu="known-faces-add" element={<AddKnownPersonPage />} />} />
        <Route path="/known-seen"      element={<Entitled menu="known-seen"  element={<KnownSeenPage />} />} />
        <Route path="/faces/banned"    element={<Entitled menu="banned"      element={<BannedFacesPage />} />} />
        <Route path="/recordings"      element={<Entitled menu="recordings"  element={<ManageRecordingsPage />} />} />
        <Route path="/admin-config"         element={<Entitled menu="admin-config" element={<AdminConfigPage />} />} />
        <Route path="/user-roles"           element={<Entitled menu="user-roles"   element={<AccessManagementPage />} />} />
        <Route path="/support"              element={<Entitled menu="user-roles" appSupport element={<SupportPage />} />} />
        <Route path="/guard-patrol"         element={<Entitled menu="guard-patrol" element={<GuardPatrolPage />} />} />
        <Route path="/guard-patrol/report"  element={<Entitled menu="guard-patrol-report" element={<PatrolReportPage />} />} />
        <Route path="/guard-patrol/test"    element={<Entitled menu="guard-patrol" element={<PatrolTestModePage />} />} />
        <Route path="/guard-checkin"        element={<Entitled menu="guard-checkin" element={<GuardCheckinPage />} />} />
        <Route path="/attendance-setup"     element={<Entitled menu="attendance-config" element={<AttendanceConfigPage />} />} />
        <Route path="/my-attendance"        element={<Entitled menu="attendance-my"     element={<MyAttendancePage />} />} />
        <Route path="/attendance-review"    element={<Entitled menu="attendance-review" element={<AttendanceReviewPage />} />} />
        <Route path="/access-events"        element={<Entitled menu="pacs-access"       industrial element={<PacsAccessPage />} />} />
        <Route path="/device-alerts"        element={<Entitled menu="device-alerts"     element={<DeviceAlertsReportPage />} />} />
        <Route path="/config-changelog"     element={<Entitled menu="config-changelog"  element={<ConfigChangelogPage />} />} />
        <Route path="/learning-report"      element={<Entitled menu="learning-report"   element={<LearningReportPage />} />} />
        <Route path="/schedule"             element={<P element={<SchedulePage />} />} />
        <Route path="/forensic"             element={<Entitled menu="analysis"    element={<ForensicReportPage />} />} />
        {/* Detection tuning is setup: not for End Users (PRVSK-141). The box refuses their saves too. */}
        <Route path="/detection-settings"   element={<Entitled menu="admin-config" element={<DetectionSettingsPage />} />} />
        <Route path="/diagnostics"          element={<Entitled menu="diagnostics" element={<DiagnosticsPage />} />} />
        <Route path="/detections"           element={<Entitled menu="detections"  element={<DetectionsPage />} />} />

        {/* ── Industrial ops console (menu-gated + clientType industrial) ── */}
        <Route path="/industrial"          element={<Entitled menu="industrial-ops"        industrial element={<IndustrialOpsPage />} />} />
        <Route path="/industrial/access"   element={<Entitled menu="industrial-access"     industrial element={<IndustrialAccessPage />} />} />
        <Route path="/industrial/attendance" element={<Entitled menu="industrial-attendance" industrial element={<IndustrialAttendancePage />} />} />
        <Route path="/industrial/wall"     element={<Entitled menu="industrial-wall"       industrial element={<IndustrialWallPage />} />} />
        <Route path="/industrial/modules"  element={<Entitled menu="industrial-modules"    industrial element={<IndustrialModulesPage />} />} />
        <Route path="/industrial/counting" element={<Entitled menu="industrial-counting"   industrial element={<IndustrialObjectCountPage />} />} />
        <Route path="/industrial/counting/report" element={<Entitled menu="industrial-count-report" industrial element={<IndustrialCountReportPage />} />} />
        <Route path="/industrial/footfall" element={<Entitled menu="industrial-footfall" industrial element={<IndustrialFootfallPage />} />} />
        <Route path="/industrial/vehicles" element={<Entitled menu="industrial-vehicle-report" industrial element={<IndustrialVehicleReportPage />} />} />
        <Route path="/industrial/seats"    element={<Entitled menu="industrial-seats"      industrial element={<IndustrialSeatsPage />} />} />
        <Route path="/industrial/seat-report" element={<Entitled menu="industrial-seat-report" industrial element={<IndustrialSeatReportPage />} />} />
        <Route path="/industrial/cleaning" element={<Entitled menu="industrial-cleaning" industrial element={<IndustrialCleaningZonesPage />} />} />
        <Route path="/industrial/cleaning/report" element={<Entitled menu="industrial-cleaning-report" industrial element={<IndustrialCleaningReportPage />} />} />
        <Route path="/industrial/reports"  element={<Entitled menu="industrial-reports"    industrial element={<IndustrialReportsPage />} />} />
        <Route path="/industrial/ppe"      element={<Entitled menu="industrial-ppe"        industrial element={<IndustrialPPEPage />} />} />
        <Route path="/industrial/analyzer" element={<Entitled menu="industrial-analyzer"   industrial element={<IndustrialVideoAnalyzerPage />} />} />
        {/* The same analyzer, scheduled over the site's own recordings. Report and
            config are separate menus: the report is a daily read, the config
            changes what the box spends its night doing. The /report route is
            declared first so it is not swallowed by /industrial/analytics. */}
        <Route path="/industrial/analytics/report" element={<Entitled menu="industrial-analytics-report" industrial element={<IndustrialShopAnalyticsReportPage />} />} />
        <Route path="/industrial/analytics" element={<Entitled menu="industrial-analytics"  industrial element={<IndustrialShopAnalyticsPage />} />} />

        <Route path="*"                element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <ThemeProvider>
        <SizeProvider>
          <SidebarProvider>
            <AppRoutes />
          </SidebarProvider>
        </SizeProvider>
      </ThemeProvider>
    </Provider>
  );
}
